import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ApiLinks } from '@cutting/app.api-links';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { HubConnection } from '@microsoft/signalr';
import {
  ChatAttachment,
  ChatMessage,
  connectToGroup,
  CuttingClientIdentification,
  CuttingOrderQuestion,
  startConnection,
  SupportChatComponent,
  SupportChatService,
} from 'cloud-shared-lib';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { OrderDetailService } from '../order-detail.service';
import { OrderQuestionChatService } from './order-question-chat.service';

@Component({
  selector: 'cutting-order-question-chat',
  templateUrl: './order-question-chat.component.html',
  standalone: true,
  imports: [SupportChatComponent, NgIf, AsyncPipe],
  providers: [{ provide: SupportChatService, useExisting: OrderQuestionChatService }],
})
export class OrderQuestionChatComponent implements OnInit, OnDestroy {
  @Input() orderId: number;
  @Input() question: CuttingOrderQuestion;
  actionButtons: { title: string; action: () => void }[] = [];

  hubConnection: HubConnection | undefined;
  sendingMessages: ChatMessage[] = [];
  markAsRead$: Observable<void>;
  questionState$: Observable<void>;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private orderQuestionChatService: OrderQuestionChatService,
    private orderDetailService: OrderDetailService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  }

  get client(): CuttingClientIdentification {
    return LocalStorageManager.client;
  }

  ngOnInit(): void {
    this.orderQuestionChatService.onQuestionChanged
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((question: CuttingOrderQuestion) => {
        if (!question) return;
        this.question = question;
        this.connectToOrderQuestionHub();
      });

    this.orderQuestionChatService.orderId = this.orderId;

    if (this.question) {
      this.updateActionButtons(this.question.closed);
    }

    this.connectToOrderQuestionHub();
  }

  private updateActionButtons(isClosed: boolean): void {
    this.actionButtons = [];
    if (isClosed) {
      this.actionButtons.push({ title: 'Возобновить обращение', action: () => this.restoreQuestion() });
    } else {
      this.actionButtons.push({ title: 'Закрыть обращение', action: () => this.closeQuestion() });
    }
  }

  private restoreQuestion(): void {
    this.questionState$ = this.orderDetailService.restoreQuestion(this.question.id).pipe(
      tap(() => {
        this.question.closed = false;

        this.orderQuestionChatService.onQuestionChanged.next(this.question);

        this.actionButtons = [];
        this.actionButtons.push({ title: 'Закрыть обращение', action: () => this.closeQuestion() });
      })
    );
  }

  private closeQuestion(): void {
    this.questionState$ = this.orderDetailService.closeQuestion(this.question.id).pipe(
      tap(() => {
        this.question.closed = true;

        this.orderQuestionChatService.onQuestionChanged.next(this.question);

        this.actionButtons = [];
        this.actionButtons.push({ title: 'Возобновить обращение', action: () => this.restoreQuestion() });
      })
    );
  }

  getSupportChatMyId(): string {
    return `CuttingClient${this.client.id}`;
  }

  public sendMessage(message: ChatMessage): void {
    this.hubConnection.invoke('Send', this.question.id, message);
    this.sendingMessages.push(message);
    this.orderQuestionChatService.newMessagesSubject.next(message);
  }

  private connectToOrderQuestionHub(): void {
    if (!this.question) {
      return;
    }

    const { connection } = startConnection(ApiLinks.orderQuestionChatHub, {
      connectHandler: () => {
        connectToGroup(this.hubConnection, this.question.id, 'Connect');
      },
    });

    this.hubConnection = connection;

    this.hubConnection.on('Send', (message: ChatMessage) => {
      try {
        this.sendingMessages.push(message);
        this.orderQuestionChatService.newMessagesSubject.next(message);
        this.markAsRead$ = this.orderQuestionChatService.markAsRead(this.question.id);
      } catch (error) {
        console.error(error);
      }
    });

    this.hubConnection.on('AttachmentsUploading', (attachments: ChatAttachment[]) => {
      this.orderQuestionChatService.newAttachmentsSubject.next(attachments);
    });
  }
}
